// @ts-nocheck
// formularz dodawania/edycji użytkownika - nadpisanie całego komponentu

import React, { FC, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import map from 'lodash/map';

import { IPostUsersRequest, IUserListItem } from 'api/types';
import { Button, FormElement, Input, Select } from 'components/controls';
import { useGetUsersRoles } from 'api';
import { useGetUsersForOrderApproval, IUserForOrderApprovalListItem } from 'api/endpoints/useGetUsersForOrderApproval';
import { usePostUsersExtended } from 'api/endpoints/usePostUsersExtended';
import { usePutUser } from 'api/endpoints/usePutUser';


import styles from 'theme/pages/Users/components/UserForm/UserForm.module.scss';

// Lokalny typ rozszerzający IPostUsersRequest o nowe pola
interface IExtendedPostUsersRequest extends IPostUsersRequest {
  order_approval_user_id?: number;
  role_id?: number;
}

// dane z rodzica przy edycji (lista nie zwraca edytowanego gdy jest exclude_user_id)
interface IInitialUserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  order_approval_user_id?: number;
  role_id?: number;
  role?: string;
}

interface IProps {
  id?: number;
  initialUserData?: IInitialUserData;
  onCancel: () => void;
  onSuccess: () => void;
}

// mapowanie roli z listy (string) na role_id w formularzu/API (Administrator = main user, id 1)
const ROLE_MAIN_USER_ID = 1;
const ROLE_TO_ID: Record<string, number> = {
  ROLE_USER_MAIN: ROLE_MAIN_USER_ID,
  ROLE_ADMIN: ROLE_MAIN_USER_ID,
  ROLE_USER: 2
};

const UserForm: FC<IProps> = ({ id, initialUserData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  
  // Zabezpieczenie przed undefined
  const userId = id || 0;
  
  const { data: usersRolesData } = useGetUsersRoles();

  // GET for-order-approval: przy edycji z exclude_user_id=<id>, przy tworzeniu bez
  const { data: orderApprovalUsersData } = useGetUsersForOrderApproval({
    page: 1,
    limit: 999,
    ...(id && { exclude_user_id: id })
  });

  // stworzenie użytkownika z order approval
  const { mutate: createUserWithOrderApproval, isLoading: isCreatingUser } = usePostUsersExtended({
    onSuccess: () => {
      onSuccess();
      onCancel();
    },
    onError: (errors) => {
      map(errors.fields_info, (field) => setUserFormFieldError(field.property_path, field.message));
    }
  });

  // aktualizacja użytkownika
  const { mutate: updateUser, isLoading: isUpdatingUser } = usePutUser(userId, {
    onSuccess: () => {
      onSuccess();
      onCancel();
    },
    onError: (errors) => {
      map(errors.fields_info, (field) => setUserFormFieldError(field.property_path, field.message));
    }
  });

  // przy edycji: dane z rodzica (lista z exclude_user_id nie zwraca edytowanego)
  const userData =
    id && initialUserData
      ? {
          first_name: initialUserData.first_name,
          last_name: initialUserData.last_name,
          email: initialUserData.email,
          order_approval_user_id: initialUserData.order_approval_user_id,
          role_id: (initialUserData.role && ROLE_TO_ID[initialUserData.role]) ?? initialUserData.role_id
        }
      : undefined;

  // initialValues tylko gdy zmieni się userData/id – inaczej enableReinitialize resetuje wybór z dropdownów
  const formInitialValues = useMemo(
    () => ({
      first_name: userData?.first_name || '',
      last_name: userData?.last_name || '',
      email: userData?.email || '',
      order_approval_user_id: userData?.order_approval_user_id ?? undefined,
      role_id: userData?.role_id ?? 2
    }),
    [id, userData?.first_name, userData?.last_name, userData?.email, userData?.order_approval_user_id, userData?.role_id]
  );

  // obsługa formularza
  const { values: userFormValues, errors: userFormErrors, setFieldValue: setUserFormFieldValue, handleSubmit: handleUserFormSubmit, setFieldError: setUserFormFieldError } = useFormik<
    Partial<IExtendedPostUsersRequest>
  >({
    initialValues: formInitialValues,
    onSubmit: (values) => {
      const userData: any = {
        first_name: values.first_name || '',
        last_name: values.last_name || '',
        email: values.email || ''
      };
      
      // order_approval_user_id: wybrany użytkownik lub null przy wyczyśczeniu (brak osoby akceptującej)
      userData.order_approval_user_id = values.order_approval_user_id ?? null;
      
      // Add role_id (default to 2 for ROLE_USER if not selected)
      userData.role_id = values.role_id || 2;
      
      // Wybierz odpowiednią funkcję w zależności od trybu
      if (userId > 0) {
        updateUser(userData);
      } else {
        createUserWithOrderApproval(userData);
      }
    },
    validateOnChange: false,
    enableReinitialize: true
  });

  const orderApprovalOptions = useMemo(() => {
    if (!orderApprovalUsersData || !orderApprovalUsersData.items || !Array.isArray(orderApprovalUsersData.items)) {
      return [];
    }
    return orderApprovalUsersData.items.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
      item: user
    }));
  }, [orderApprovalUsersData]);

  // Opcje ról: Administrator = main user (id 1), Użytkownik (id 2)
  const roleOptions = [
    { value: ROLE_MAIN_USER_ID, label: t('Administrator'), item: ROLE_MAIN_USER_ID },
    { value: 2, label: 'Użytkownik', item: 2 }
  ];

  const isLoading = isCreatingUser || isUpdatingUser;

  return (
    <div
      className={classnames(styles.wrapperComponent, 'StylePath-Pages-Users-components-UserForm')}>
      <FormElement>
        <label>
          <Trans>Imię</Trans>
        </label>
        <Input
          value={userFormValues.first_name || ''}
          onChange={(value) => setUserFormFieldValue('first_name', value)}
          error={userFormErrors.first_name}
        />
      </FormElement>

      <FormElement>
        <label>
          <Trans>Nazwisko</Trans>
        </label>
        <Input
          value={userFormValues.last_name || ''}
          onChange={(value) => setUserFormFieldValue('last_name', value)}
          error={userFormErrors.last_name}
        />
      </FormElement>

      <FormElement>
        <label>
          <Trans>Email</Trans>
        </label>
        <Input
          value={userFormValues.email || ''}
          onChange={(value) => setUserFormFieldValue('email', value)}
          error={userFormErrors.email}
        />
      </FormElement>

      <FormElement>
        <label>
          <Trans>Użytkownik akceptujący zamówienia</Trans>
        </label>
        <Select<IUserForOrderApprovalListItem>
          onChange={(item) => setUserFormFieldValue('order_approval_user_id', item?.id)}
          value={userFormValues.order_approval_user_id ?? null}
          options={orderApprovalOptions}
          error={userFormErrors.order_approval_user_id}
          placeholder={<Trans>Wybierz użytkownika</Trans>}
          clearable
        />
      </FormElement>

      <FormElement>
        <label>
          <Trans>Rola</Trans>
        </label>
        <Select
          onChange={(option) => setUserFormFieldValue('role_id', option || 2)}
          value={userFormValues.role_id || 2}
          options={roleOptions}
          error={userFormErrors.role_id}
          placeholder={<Trans>Wybierz rolę</Trans>}
        />
      </FormElement>

      <div className={styles.actions}>
        <Button onClick={onCancel} ghost>
          <Trans>Anuluj</Trans>
        </Button>
        <Button loading={isLoading} onClick={() => handleUserFormSubmit()}>
          <Trans>{userId > 0 ? 'Zaktualizuj' : 'Zapisz'}</Trans>
        </Button>
      </div>
    </div>
  );
};

export default UserForm;
